/**
 * String lookup and interpolation. No library.
 *
 * The whole i18n "framework" is this file plus two typed catalogs. What makes it
 * safe is not runtime machinery but the type annotation on the Spanish catalog:
 * `Record<keyof typeof en, string>` means `tsc` fails the build if a Spanish string
 * is missing. A missing translation is caught at authoring time, which is the only
 * time it is cheap to fix.
 *
 * Checklist item text is not here. It lives in the database and ships in the site
 * bundle with every language inlined, because the device has to switch language with
 * no signal, and because legal needs to revise the OSHA wording without a release.
 */

export type Catalog = Readonly<Record<string, string>>;

export type TranslationVars = Readonly<Record<string, string | number>>;

/**
 * Looks `key` up in each catalog in turn.
 *
 * Callers pass the operator's language first and English second. Falling back a whole
 * string at a time is deliberate: a sentence half in Spanish and half in English is
 * harder to read than one consistently in the wrong language.
 *
 * A key that exists nowhere is returned as itself. An operator can read
 * "service_brakes" aloud to a supervisor; a blank line tells them nothing.
 */
export function translate(
  catalogs: readonly Catalog[],
  key: string,
  vars: TranslationVars = {},
): string {
  const template = catalogs.find((catalog) => key in catalog)?.[key];
  return interpolate(template ?? key, vars);
}

/**
 * English and Spanish share the same one/other rule, so this needs no CLDR data.
 * A language with a different rule -- Polish, Arabic -- would need more than a new
 * catalog, and that is the point at which to reach for a real library.
 */
export function translatePlural(
  catalogs: readonly Catalog[],
  key: string,
  count: number,
): string {
  const form = count === 1 ? 'one' : 'other';
  return translate(catalogs, `${key}_${form}`, { count });
}

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(template: string, vars: TranslationVars): string {
  return template.replace(PLACEHOLDER, (placeholder, name: string) => {
    const value = vars[name];
    // An unmatched placeholder stays visible. A {name} on screen gets reported by
    // whoever sees it; a silently blanked one ships.
    return value === undefined ? placeholder : String(value);
  });
}
